import React from 'react';
import { KTCard, KTCardBody } from '../../../../_metronic/helpers';
import { useIntl } from 'react-intl';
import { useGetRecentTransactionsQuery, useGetSubscriptionQuery } from '../../../store/services/apuestesAPI';
import type {
  SubscriptionUser
} from '../../../store/types/dashboard.types';

const AnalyticsTables: React.FC = () => {
  const intl = useIntl();
  const { data: transactionsData, isLoading: transactionsLoading, error: transactionsError } = useGetRecentTransactionsQuery();
  const { data: subscriptionData, isLoading: subscriptionLoading, error: subscriptionError } = useGetSubscriptionQuery();
  
  const isLoading = transactionsLoading || subscriptionLoading;
  const hasError = transactionsError || subscriptionError;

  // Función para procesar datos de transacciones recientes
  const processTransactionsData = () => {
    if (!transactionsData || !Array.isArray(transactionsData)) {
      return [];
    }

    // Procesar las transacciones reales
    const processedTransactions = transactionsData.map((transaction: any) => {
      const user = transaction.user;
      const userName = user?.alias || `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || user?.email || 'Usuario';
      const amount = transaction.amount || 0;
      const status = transaction.status || 'UNKNOWN';
      const description = transaction.description || 'Transacción';
      const type = transaction.type || 'CREDIT';
      const source = transaction.source || 'UNKNOWN';
      const createdAt = new Date(transaction.created_at).toLocaleDateString('es-ES');
      
      // Determinar color del estado
      let statusColor = 'secondary';
      if (status === 'SUCCEEDED') statusColor = 'success';
      else if (status === 'PENDING') statusColor = 'warning';
      else if (status === 'FAILED') statusColor = 'danger';

      return {
        id: transaction.id,
        user: userName,
        amount: `$${amount.toFixed(2)}`,
        type: description,
        status: status,
        date: createdAt,
        statusColor: statusColor,
        source: source,
        transactionType: type
      };
    });

    return processedTransactions.slice(0, 5); // Mostrar solo las 5 más recientes
  };

  // Función para procesar datos de suscripciones
  const processSubscriptionData = (): Array<{
    country: string;
    users: number;
    percentage: string;
    flag: string;
  }> => {
    if (!subscriptionData || !subscriptionData.data || !Array.isArray(subscriptionData.data)) {
      return [];
    }

    const subscriptions: SubscriptionUser[] = subscriptionData.data;
    const total = subscriptionData.total || subscriptions.length;

    // Agrupar por tipo de suscripción (usando subscription.name o subscription.type)
    const subscriptionGroups = subscriptions.reduce((acc: Record<string, { 
      count: number; 
      name: string;
      totalAmount: number;
    }>, sub: SubscriptionUser) => {
      // Usar el nombre del tipo de suscripción o un identificador único
      const subscriptionName = sub.subscription?.name || 
                              sub.subscription?.type || 
                              sub.subscription?.title || 
                              'Suscripción Básica';
      
      if (!acc[subscriptionName]) {
        acc[subscriptionName] = {
          count: 0,
          name: subscriptionName,
          totalAmount: 0
        };
      }
      acc[subscriptionName].count++;
      // Sumar el monto si está disponible
      if (sub.subscription?.price || sub.subscription?.amount) {
        acc[subscriptionName].totalAmount += sub.subscription.price || sub.subscription.amount || 0;
      }
      return acc;
    }, {});

    // Convertir a array y calcular porcentajes
    const subscriptionArray = Object.values(subscriptionGroups).map((group) => ({
      country: group.name,
      users: group.count,
      percentage: `${((group.count / total) * 100).toFixed(1)}%`,
      flag: getSubscriptionFlag(group.name)
    }));

    // Ordenar por cantidad y tomar los primeros 5
    return subscriptionArray
      .sort((a, b) => b.users - a.users)
      .slice(0, 5);
  };


  // Función auxiliar para obtener emojis de tipos de suscripción
  const getSubscriptionFlag = (subscriptionName: string) => {
    const flags: { [key: string]: string } = {
      'premium': '⭐',
      'basic': '📋',
      'pro': '🚀',
      'enterprise': '🏢',
      'monthly': '📅',
      'yearly': '📆',
      'lifetime': '💎',
      'suscripción básica': '📋',
      'suscripción premium': '⭐',
      'suscripción pro': '🚀'
    };
    return flags[subscriptionName.toLowerCase()] || '💳';
  };

  const transactions = processTransactionsData();
  const topCountries = processSubscriptionData();

  if (isLoading) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-8">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <KTCard className="h-100">
            <KTCardBody className="d-flex flex-center p-8">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
                <div className="text-muted">Error al cargar los datos</div>
                <small className="text-muted">
                  {transactionsError && `Transacciones: ${JSON.stringify(transactionsError)}`}
                  {subscriptionError && `Suscripciones: ${JSON.stringify(subscriptionError)}`}
                </small>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-4">
      {/* Top Pages Table */}
      <div className="col-lg-6 col-xl-6">
        <KTCard className="h-md-100">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">{intl.formatMessage({ id: 'DASHBOARD.TABLES.RECENT_TRANSACTIONS' })}</span>
              <span className="text-muted fw-semibold fs-7">{intl.formatMessage({ id: 'DASHBOARD.TABLES.RECENT_TRANSACTIONS_DESC' })}</span>
            </h3>
          </div>
          <KTCardBody className="pt-0">
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th className="min-w-200px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.USER' })}</th>
                    <th className="min-w-150px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.TYPE' })}</th>
                    <th className="min-w-100px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.AMOUNT' })}</th>
                    <th className="min-w-100px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.STATUS' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <tr key={transaction.id || index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-40px me-3">
                              <div className="symbol-label bg-light-primary">
                                <span className="text-primary fw-bold">
                                  {transaction.user.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="d-flex justify-content-start flex-column">
                              <div className="text-gray-800 fw-bold text-hover-primary fs-6">
                                {transaction.user}
                              </div>
                              <div className="text-muted fs-7">{transaction.date}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="text-gray-800 fw-bold fs-6">
                            {(() => {
                              // Traducir el type (description) de la transacción
                              try {
                                const translationKey = `PAYMENT.EVENT.${transaction.type}`;
                                const translated = intl.formatMessage({ id: translationKey });
                                // Si la traducción es diferente a la clave, significa que existe
                                if (translated !== translationKey) {
                                  return translated;
                                }
                              } catch (e) {
                                // Si no existe la traducción, usar el valor original
                              }
                              return transaction.type;
                            })()}
                          </div>
                          <div className="text-muted fs-7">{transaction.source}</div>
                        </td>
                        <td>
                          <div className="text-gray-800 fw-bold fs-6">{transaction.amount}</div>
                        </td>
                        <td>
                          <span className={`badge badge-light-${transaction.statusColor} fw-bold`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <div className="text-muted">
                          <i className="fas fa-info-circle me-2"></i>
                          {intl.formatMessage({ id: 'DASHBOARD.COMMON.NO_TRANSACTIONS' })}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      {/* Top Countries Table */}
      <div className="col-lg-6 col-xl-6">
        <KTCard className="h-md-100">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">{intl.formatMessage({ id: 'DASHBOARD.TABLES.SUBSCRIPTION_TYPES' })}</span>
              <span className="text-muted fw-semibold fs-7">{intl.formatMessage({ id: 'DASHBOARD.TABLES.SUBSCRIPTION_TYPES_DESC' })}</span>
            </h3>
          </div>
          <KTCardBody className="pt-0">
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th className="min-w-200px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.PLAN' })}</th>
                    <th className="min-w-100px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.SUBSCRIPTIONS' })}</th>
                    <th className="min-w-100px">{intl.formatMessage({ id: 'DASHBOARD.TABLES.PERCENTAGE' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {topCountries.map((subscription, index) => (
                    <tr key={index}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-45px me-3">
                            <span className="symbol-label fs-2x">{subscription.flag}</span>
                          </div>
                          <div className="d-flex justify-content-start flex-column">
                            <div className="text-gray-800 fw-bold text-hover-primary fs-6">
                              {subscription.country}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-gray-800 fw-bold fs-6">{subscription.users.toLocaleString()}</div>
                      </td>
                      <td>
                        <div className="text-gray-800 fw-bold fs-6">{subscription.percentage}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </KTCardBody>
        </KTCard>
      </div>
    </div>
  );
};

export { AnalyticsTables };
